import { Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Building2, FileText, HelpCircle, Star } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { useDepartmentsWithTemplatesQuery } from "../api/queries";
import { Button } from "@devhop/ui";
import {
  TemplateByDepartmentTour,
  useTemplateByDepartmentTour,
} from "../tour/TemplateByDepartmentTour";
import { TemplateByDepartmentFilter } from "../ui/TemplateByDepartmentFilter";

export function TemplateByDepartmentPage() {
  const nav = useNavigate();
  const search: OffsetPageQueryDTO = useSearch({ strict: false });
  const tour = useTemplateByDepartmentTour();

  const list = useDepartmentsWithTemplatesQuery({
    offset: search.offset,
    limit: search.limit ?? 50, // Show more departments by default
    sort: search.sort,
    filters: search.filters,
  });

  const handleViewTemplate = (templateId: string) => {
    nav({ to: `/app/template/${templateId}` });
  };

  const handleViewDepartmentTemplates = (departmentId: string) => {
    // Navigate to main template list filtered by department
    nav({
      to: "/app/template",
      search: {
        filters: [{ field: "departmentId", op: "eq", value: departmentId }],
      },
    });
  };

  return (
    <>
      <Header />

      <Main>
        <TemplateByDepartmentTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div className="space-y-6">
          {/* Header */}
          <div
            className="flex items-start justify-between"
            data-tourid="by-department-header"
          >
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-bold text-3xl">ຮ່າງເອກະສານຕາມພະແນກ</h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="mt-1 text-muted-foreground">
                ເລືອກພະແນກເພື່ອເບິ່ງຮ່າງເອກະສານທີ່ສາມາດໃຊ້ໄດ້.
              </p>
            </div>
          </div>

          {/* Filter */}
          <TemplateByDepartmentFilter />

          {/* Department Cards */}
          {list.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
            </div>
          ) : list.data?.data.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FileText className="mb-4 h-12 w-12 text-muted-foreground" />
                <p className="text-muted-foreground">
                  ບໍ່ພົບພະແນກທີ່ມີຮ່າງເອກະສານ
                </p>
              </CardContent>
            </Card>
          ) : (
            <div
              className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
              data-tourid="by-department-cards"
            >
              {list.data?.data.map((dept) => (
                <Card key={dept.id} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-primary" />
                        <CardTitle className="text-lg">{dept.name}</CardTitle>
                      </div>
                      <div className="rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                        {dept.templateCount} ຮ່າງ
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Frequently Used Templates */}
                    {dept.frequentlyUsedTemplates.length > 0 ? (
                      <div
                        className="space-y-2"
                        data-tourid="by-department-frequently-used"
                      >
                        <div className="space-y-1.5">
                          {dept.frequentlyUsedTemplates.map((template) => (
                            <button
                              key={template.id}
                              type="button"
                              onClick={() => handleViewTemplate(template.id)}
                              className="flex w-full items-center justify-between rounded-md border p-2 text-left hover:bg-accent transition-colors"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                                  <span className="truncate text-sm">
                                    {template.name}
                                  </span>
                                  {template.isRecommended && (
                                    <Star className="h-3 w-3 shrink-0 fill-yellow-400 text-yellow-400" />
                                  )}
                                </div>
                                {template.categoryName && (
                                  <span className="text-muted-foreground text-xs">
                                    {template.categoryName}
                                  </span>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">
                        ບໍ່ມີຮ່າງເອກະສານ
                      </p>
                    )}

                    {/* View All Button */}
                    {dept.templateCount > dept.frequentlyUsedTemplates.length && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => handleViewDepartmentTemplates(dept.id)}
                        data-tourid="by-department-view-all"
                      >
                        ເບິ່ງຮ່າງເອກະສານທັງໝົດ ({dept.templateCount})
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </Main>
    </>
  );
}
